// ============================================
// 📲 Which account gets THIS phone's notifications
// src/utils/pushDevice.js
//
// A phone's push "address" belongs to the installed app, not to an account.
// So:
//   • claimThisDevice()  — on every app open while logged in: this phone now
//     belongs to the account that's logged in (the server moves it over).
//   • forgetThisDevice() — on Log out: the server stops sending this phone
//     that account's alerts. Must run BEFORE the login is cleared.
// Skipped while an admin is impersonating ("Login as"), so the admin's phone
// never starts receiving a client's alerts.
// ============================================
import config from '../config';

const token = () => { try { return localStorage.getItem('token'); } catch (e) { return null; } };
const impersonating = () => { try { return localStorage.getItem('isImpersonating') === 'true'; } catch (e) { return false; } };

async function currentSubscription() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
    const reg = await navigator.serviceWorker.getRegistration();
    return reg ? await reg.pushManager.getSubscription() : null;
  } catch (e) { return null; }
}

// never let a slow network hold up logging out
const withTimeout = (p, ms) => Promise.race([p, new Promise(r => setTimeout(r, ms))]);

export async function claimThisDevice(deviceName = '') {
  try {
    if (!token() || impersonating()) return false;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
    const sub = await currentSubscription();
    if (!sub) return false;
    const r = await fetch(`${config.API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription: sub.toJSON(), device: deviceName })
    }).then(x => x.json()).catch(() => null);
    return !!r?.success;
  } catch (e) { return false; }
}

export async function forgetThisDevice() {
  try {
    if (!token() || impersonating()) return;
    const sub = await currentSubscription();
    if (!sub) return;
    await withTimeout(fetch(`${config.API_URL}/api/push/unsubscribe`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint })
    }).catch(() => {}), 2500);
    // The phone keeps its permission and subscription, so the next person who
    // logs in here gets their own alerts without being asked again.
  } catch (e) { /* logging out must never fail because of this */ }
}